/**
 * Main UI logic for sliding drawer to launch jobs.
 */
import {Popover} from '@material-ui/core';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, IconInfo} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useNodeValue} from '@wandb/weave/react';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import YAML from '../../../../services/yaml';
import {
  RunFilesAndFolderWithDirectUrLsQueryResult,
  RunQueueAccessType,
  RunQueuePrioritizationMode,
  TemplateVariable,
  useCreateDefaultResourceConfigMutation,
  useCreateRunQueueMutation,
  usePushToRunQueueMutation,
  useRunFilesAndFolderWithDirectUrLsQuery,
  useUpsertModelMutation,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {jsonParseSafe} from '../../../util/json';
import {queue as queueUrl} from '../../../util/urls';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {CodeEditor} from '../CodeEditor';
import {
  DEFAULT_LAUNCH_PROJECT_NAME,
  PRIORITY_LEVEL,
  PRIORITY_LEVEL_METADATA,
} from '../constants';
import {
  isSingleVarString,
  sortTemplateVariablesByConfig,
} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import ResourceVariableForm from '../DefaultResourceConfig/ResourceVariableForm';
import {useDestinationProjectOptions, useRunQueueItem} from '../hooks';
import {
  getJobDefinitionFileOp,
  getJobMetadataOp,
  JobFile,
} from '../JobDetail/utilsJobFile';
import {JobDetails, JobRun, JobVersions} from '../JobDetail/utilTypes';
import {isQueuePrioritizationEnabled} from '../Queues/queueUtil';
import {SelectPriority} from '../SelectPriority';
import {ProjectOption, SelectProject} from '../SelectProject2';
import {GroupedQueueOptions, QueueOption, SelectQueue} from '../SelectQueue2';
import {isWandbJobsProject, WANDB_ENTITY_NAME} from '../utils';
import {Json, JsonObject, LaunchSpec} from '../utilTypes';
import * as S from './JobDrawerContentInner.styles';
import {JobDrawerOverrides} from './JobDrawerOverrides';
import {JobDrawerStructuredOverrides} from './JobDrawerStructuredOverrides';
import {SelectRunPanel} from './SelectRunPanel';

type JobDrawerContentInnerProps = React.PropsWithChildren<{
  entityName: string;
  projectName: string;
  job: JobDetails;
  runToClone?: string;
  lockedRunToClone?: boolean;
  customRunColors: Record<string, string>;
  queues: GroupedQueueOptions[];
  finalizeCTA?: string;
  overrideHintText?: string;
  onFinalize?: (spec: LaunchSpec) => void;
  onClose: () => void;
}>;

export const getDefaultCloneFrom = (
  runs: JobRun[],
  username: string,
  runToClone?: string
): JobRun | null => {
  if (runToClone) {
    for (const run of runs) {
      if (run.name === runToClone) {
        return run;
      }
    }
  }
  // Runs are sorted creation date descending, so the first run is the most recent.
  // TODO: Some debate over whether this should take job success into account.
  for (const run of runs) {
    if (run.creator?.username === username) {
      return run;
    }
  }
  if (runs.length > 0) {
    return runs[0];
  }
  return null;
};

const findMatchingQueue = ({
  queueName,
  queueEntity,
  queueOptions,
}: {
  queueName: string;
  queueEntity: string;
  queueOptions: GroupedQueueOptions[];
}) => {
  const entityQueue = queueOptions.find(q => q.label === queueEntity);
  if (entityQueue == null) {
    return null;
  }
  const queue = entityQueue.options.find(q => q.queue === queueName);
  if (queue != null && queue.isActive && !queue.disabled) {
    return queue;
  }
  return null;
};

const getDefaultQueue = (
  queues: GroupedQueueOptions[],
  runQueueName?: string | null,
  entityQueueName?: string | null
): QueueOption | null => {
  if (queues.length === 0) {
    return null;
  }

  if (entityQueueName != null && runQueueName != null) {
    const queue = findMatchingQueue({
      queueName: runQueueName,
      queueEntity: entityQueueName,
      queueOptions: queues,
    });
    if (queue != null) {
      return queue;
    }
  }

  const group = queues[0];
  if (group.options.length === 0) {
    return null;
  }
  const option = group.options[0];
  if (option.disabled || !option.isActive) {
    return null;
  }
  return option;
};

export const getVersionDescription = (
  versions: JobVersions,
  runTarget: string
) => {
  for (const version of versions) {
    if (version.aliases.some(alias => alias.alias === runTarget)) {
      return version.description;
    }
  }
  return '';
};

const getDefaultVariableValuesFromQueueVariables = (
  variables: TemplateVariable[]
) => {
  const z = variables.reduce((acc, variable) => {
    const parsedSchema = JSON.parse(variable.schema);
    return {
      ...acc,
      ...(parsedSchema.default != null && {
        [variable.name]: parsedSchema.default,
      }),
    };
  }, {});
  return z;
};

const determineQueueInfoFromRunConfig = (run: JobRun | null) => {
  if (!run) {
    return {runsQueueName: null, runsEntityName: null, runQueueItemID: null};
  }
  // TODO: fix up the types for JsonObject to make this less hacky
  const wandbConfig: {[property: string]: Json} = (
    run.config._wandb as {[property: string]: Json}
  )?.value as {[property: string]: Json};
  const runsQueueName = wandbConfig?.launch_queue_name as string;
  const runQueueItemID = wandbConfig?.launch_trace_id as string;
  return {runsQueueName, runsEntityName: run.entityName, runQueueItemID};
};

const pruneTemplateVariableValues = (
  variableValues: Record<string, number | string>,
  queueVariables: TemplateVariable[]
) => {
  const allowed = queueVariables.map(value => {
    return value.name;
  });
  const prunedValues = Object.keys(variableValues)
    .filter(key => allowed.includes(key))
    .reduce((obj: Record<string, number | string>, key) => {
      obj[key] = variableValues[key];
      return obj;
    }, {});
  return prunedValues;
};

const populateJobFilesMap = async (
  queryRunFiles: RunFilesAndFolderWithDirectUrLsQueryResult
): Promise<{[x: string]: JsonObject}> => {
  const files = queryRunFiles.data?.project?.run?.files?.edges ?? [];
  const fetchPromises = files.map(async edge => {
    if (!edge.node || !edge.node.name || !edge.node.directUrl) {
      console.error('Invalid file edge:', edge);
      return null;
    }

    try {
      const response = await fetch(edge.node.directUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      // Get response as text to handle JSON or YAML
      let parsedData;
      try {
        // Responses can only be read once, so clone the response before reading in case
        // we need to read it again to parse as yaml
        parsedData = await response.clone().json();
      } catch {
        try {
          const content = await response.text();
          parsedData = YAML.parse(content);
        } catch {
          console.error(`Failed to parse file ${edge.node.name}`);
          return null;
        }
      }

      return {name: edge.node.name, data: parsedData};
    } catch (error) {
      console.error(`Failed to fetch or parse file ${edge.node.name}:`, error);
      return null;
    }
  });

  const results = await Promise.allSettled(fetchPromises);
  const newFilesMap: {[x: string]: any} = {};
  results.forEach(r => {
    if (r.status === 'fulfilled' && r.value) {
      const name = r.value.name.substring(r.value.name.indexOf('/') + 1);
      newFilesMap[name] = r.value.data;
    } else {
      console.error(`Failed to fetch file`);
    }
  });
  return newFilesMap;
};

enum OverridesMode {
  BASIC = 'basic',
  ADVANCED = 'advanced',
}

export const JobDrawerContentInner = ({
  entityName,
  projectName,
  job,
  runToClone,
  lockedRunToClone,
  customRunColors,
  queues,
  finalizeCTA,
  children,
  overrideHintText,
  onFinalize,
  onClose,
}: JobDrawerContentInnerProps) => {
  const viewer = useViewer();
  const viewerEntity = viewer?.username ?? '';
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine which runs to show the user as clone options.
  const previousRuns = useMemo(
    () => job.runs.filter(run => run.version === job.versionInfo.runTargetNum),
    [job.runs, job.versionInfo.runTargetNum]
  );
  const defaultCloneRun = useMemo(
    () => getDefaultCloneFrom(previousRuns, viewer?.username ?? '', runToClone),
    [previousRuns, viewer?.username, runToClone]
  );
  const [cloneRun, setCloneRun] = useState<JobRun | null>(defaultCloneRun);

  // When user edits the overrides, clear any previous errors
  const [overrides, setOverrides] = useState('');
  const [overridesError, setOverridesError] = useState('');
  const [filesMap, setFilesMap] = useState<{[x: string]: any}>({});
  const onOverridesChange = useCallback((value: string) => {
    setOverrides(value);
    setOverridesError('');
  }, []);
  const [structuredOverrides, setStructuredOverrides] = useState<JsonObject>(
    overrides ? jsonParseSafe(overrides)?.run_config : {}
  );
  const [structuredFileOverrides, setStructuredFileOverrides] = useState<
    JsonObject | undefined
  >(overrides ? jsonParseSafe(overrides)?.files : {});
  const onStructuredOverridesChange = useCallback(
    (config: JsonObject, files?: JsonObject) => {
      const overridesStr = JSON.stringify(
        {
          args: [],
          entry_point: [],
          run_config: config,
          files,
        },
        null,
        4
      );
      setOverrides(overridesStr);
      setStructuredOverrides(config);
      setStructuredFileOverrides(files);
      setOverridesError('');
    },
    []
  );
  const {
    runsQueueName,
    runsEntityName,
    runQueueItemID: runQueueItemToClone,
  } = determineQueueInfoFromRunConfig(cloneRun);
  const defaultQueue = useMemo(
    () => getDefaultQueue(queues, runsQueueName, runsEntityName),
    [queues, runsQueueName, runsEntityName]
  );
  const defaultVariables = defaultQueue?.variables ?? [];
  const [selectedQueue, setSelectedQueue] = useState<QueueOption | null>(
    defaultQueue
  );
  const [overridesMode, setOverridesMode] = useState(OverridesMode.BASIC);
  const variantBasic =
    overridesMode === OverridesMode.BASIC ? 'secondary' : 'ghost';
  const variantAdvanced =
    overridesMode === OverridesMode.ADVANCED ? 'secondary' : 'ghost';

  const setConfig = useCallback(
    (
      config: JsonObject,
      args?: string[],
      entrypoint?: string[],
      files?: JsonObject
    ) => {
      const newOverrides: {[x: string]: any} = {
        args,
        run_config: config,
        entry_point: entrypoint,
        files,
      };

      if (files != null && Object.keys(files).length > 0) {
        newOverrides.files = {...files};
      }
      const overrideStr = JSON.stringify(newOverrides, null, 4);
      onOverridesChange(overrideStr);
      onStructuredOverridesChange(config, files);
    },
    [onOverridesChange, onStructuredOverridesChange]
  );
  const [templateVariables, setTemplateVariables] =
    useState<TemplateVariable[]>(defaultVariables);
  const [variableValues, setVariableValues] = useState<
    Record<string, number | string>
  >(getDefaultVariableValuesFromQueueVariables(defaultVariables));

  const updateVariable = useCallback(
    (name: string, value: string | number) => {
      const templateVar = templateVariables.find(tv => tv.name === name);
      if (templateVar == null) {
        toast(
          'Resource config value not saved, please select resource config values'
        );
        return;
      }
      let updateValue = value;
      try {
        const tvSchema = JSON.parse(templateVar.schema);
        if (tvSchema.type === 'number' || tvSchema.type === 'integer') {
          updateValue = Number(value);
        }
      } catch {
        toast(
          `Invalid schema for variable: ${name} please reconfigure queue by visiting the queue tab.`
        );
        return;
      }
      setVariableValues({
        ...variableValues,
        [name]: updateValue,
      });
    },
    [templateVariables, variableValues]
  );

  const contents = useMemo(
    () =>
      getJobDefinitionFileOp({
        entityName: job.entityName,
        projectName: job.projectName,
        jobName: job.name,
        alias: job.versionInfo.runTarget ?? '',
      }),
    [job.entityName, job.projectName, job.name, job.versionInfo.runTarget]
  );
  const metadataContents = useMemo(
    () =>
      getJobMetadataOp({
        entityName: job.entityName,
        projectName: job.projectName,
        jobName: job.name,
        alias: job.versionInfo.runTarget ?? '',
      }),
    [job.entityName, job.projectName, job.name, job.versionInfo.runTarget]
  );

  const {result: jobDefinitionStr, loading} = useNodeValue(contents);
  const {result: jobMetadata, loading: metadataLoading} =
    useNodeValue(metadataContents);
  // new jobs have input and output shape on metadata instead of job def file
  const usesJobArtifactMetadata = jobMetadata?.input_types != null;
  const [showResourceConfig, setShowResourceConfig] = useState(false);
  const [resourceConfig, setResourceConfig] = useState('{}');
  const [resourceConfigError, setResourceConfigError] = useState('');
  const onResourceConfigChange = (value: string) => {
    setResourceConfig(value);
    setResourceConfigError('');
  };
  const [displayMode, setDisplayMode] = useState('json');
  const variantYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const variantJson = displayMode === 'json' ? 'secondary' : 'ghost';
  const sortedTemplateVariables = useMemo(() => {
    if (selectedQueue != null) {
      return sortTemplateVariablesByConfig(
        JSON.stringify(selectedQueue?.config),
        templateVariables
      );
    }
    return [];
  }, [selectedQueue, templateVariables]);
  const queueUsingTemplateVariables = templateVariables.length > 0;

  const setEditorType = (type: string) => {
    if (displayMode === type) {
      return;
    }
    // Convert the blob from previous format to current format
    if (type === 'json') {
      try {
        let parsed = YAML.parse(resourceConfig);
        if (!parsed) {
          parsed = {};
        }
        setResourceConfig(JSON.stringify(parsed, null, 2));
        setDisplayMode('json');
      } catch (e) {
        toast.error('Invalid YAML');
        return;
      }
    } else if (type === 'yaml') {
      try {
        let parsed = JSON.parse(resourceConfig);
        if (resourceConfig === '{}') {
          parsed = undefined; // show nothing for yaml when empty
        }
        setResourceConfig(YAML.stringify(parsed, {indent: 2}));
        setDisplayMode('yaml');
      } catch (e) {
        toast.error('Invalid JSON');
        return;
      }
    }
  };

  const [upsertProject] = useUpsertModelMutation();
  const [createDefaultResourceConfig] =
    useCreateDefaultResourceConfigMutation();
  const [createRunQueue] = useCreateRunQueueMutation();
  const [pushToRunQueue] = usePushToRunQueueMutation({
    context: propagateErrorsContext(),
  });
  const [priority, setPriority] = useState(PRIORITY_LEVEL.MEDIUM);
  const priorityMetadata = useMemo(
    () => PRIORITY_LEVEL_METADATA[priority],
    [priority]
  );

  // TODO(np): Actually hook this up once queue prioritization mode info
  // is available from the backend.  For now, just say that only docker queues
  // support prioritization.
  const queueSupportsPrioritization = useMemo(
    () => selectedQueue != null && isQueuePrioritizationEnabled(selectedQueue),
    [selectedQueue]
  );

  // If the selected queue doesn't support prioritization, reset the priority
  // Avoid potentially confusing visual state where the disabled priority selector
  // shows a priority that would actually be ignored by backend.
  const effectivePriority = useMemo(
    () => (queueSupportsPrioritization ? priority : PRIORITY_LEVEL.MEDIUM),
    [priority, queueSupportsPrioritization]
  );

  const inWandbJobs = isWandbJobsProject(entityName, projectName);
  const [targetEntityName, setTargetEntityName] = useState(
    inWandbJobs ? '' : entityName
  );
  const [targetProjectName, setTargetProjectName] = useState(
    inWandbJobs ? '' : projectName
  );
  const onSelectProject = (option: ProjectOption) => {
    setTargetEntityName(option.entityName);
    setTargetProjectName(option.projectName);
  };
  const queryTargetProjects = useDestinationProjectOptions(
    projectName,
    selectedQueue?.entity ?? ''
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const onPopoverClose = () => {
    setAnchorEl(null);
  };

  const substituteVariables = (
    config: JsonObject,
    variables: Record<string, number | string>
  ): JsonObject => {
    function injectVarsIntoString(str: string): string | number {
      let singleValue: string | number | null = null;
      const replaceString = str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        singleValue = variables[varName];
        return variables[varName]?.toString() ?? match;
      });
      if (isSingleVarString(str) && singleValue != null) {
        return singleValue;
      }
      return replaceString;
    }

    function substitute(obj: any): any {
      if (typeof obj === 'string') {
        return injectVarsIntoString(obj);
      } else if (typeof obj === 'number') {
        return obj;
      } else if (Array.isArray(obj)) {
        return obj.map(item => substitute(item));
      } else if (typeof obj === 'object' && obj !== null) {
        return Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [key, substitute(val)])
        );
      }
      return obj;
    }

    return substitute(config);
  };

  const onSelectQueue = useCallback((option: QueueOption) => {
    setSelectedQueue(option);
    setTemplateVariables(option.variables);
    setVariableValues(
      getDefaultVariableValuesFromQueueVariables(option.variables)
    );
  }, []);

  useEffect(() => {
    // reset clone run if the current cloned run not in list of runs for version
    // and is not null (meaning the user didn't edit it)
    const newCloneRun = getDefaultCloneFrom(
      previousRuns,
      viewer?.username ?? '',
      cloneRun?.name
    );
    if (
      newCloneRun != null &&
      cloneRun != null &&
      newCloneRun.name !== cloneRun?.name
    ) {
      setCloneRun(newCloneRun);
    }
    if (
      !jobDefinitionStr ||
      Boolean(overrides) ||
      Object.keys(structuredOverrides).length > 0
    ) {
      // We need the job definition string for both defaults and ordering.
      return;
    }

    const jobfile = new JobFile(jobDefinitionStr);
    setConfig(jobfile.getConfigInputExample(), [], []);
  }, [
    cloneRun,
    previousRuns,
    viewer?.username,
    jobDefinitionStr,
    overrides,
    structuredOverrides,
    setConfig,
  ]);

  const queryRunQueueItem = useRunQueueItem(
    entityName,
    DEFAULT_LAUNCH_PROJECT_NAME,
    defaultQueue?.queue ?? '',
    runQueueItemToClone ?? ''
  );

  const queryRunFiles = useRunFilesAndFolderWithDirectUrLsQuery({
    variables: {
      projectName,
      entityName,
      runName: cloneRun?.name ?? '',
      maxNumFiles: 10,
      pattern: '_wandb_configs/_%',
    },
  });

  useEffect(() => {
    const loadFilesMap = async () => {
      const updatedFilesMap = await populateJobFilesMap(queryRunFiles);
      setFilesMap(updatedFilesMap);
    };
    loadFilesMap();
  }, [queryRunFiles]);

  // update overrides block when job or cloned run changes
  // could happen when user selects new job
  // or when user selects a run to clone outside of the
  // drawer eg. from the runs table
  useEffect(() => {
    if (!cloneRun) {
      return;
    }
    const rqiOverrides = queryRunQueueItem.runQueueItem?.runSpec?.overrides;
    if (!jobDefinitionStr) {
      return;
    }
    const jobfile = new JobFile(jobDefinitionStr, jobMetadata);
    const orderedConfig = jobfile.getOrderedClonedRunConfig(cloneRun.config);
    const prunedFilesMap = jobfile.getPrunedFilesMap(filesMap);
    if (
      runQueueItemToClone &&
      rqiOverrides &&
      !queryRunQueueItem.loading &&
      !queryRunQueueItem.error
    ) {
      const runConfig = rqiOverrides.run_config ?? orderedConfig;
      const filesValue = rqiOverrides.files ?? prunedFilesMap;
      setConfig(
        runConfig,
        rqiOverrides.args,
        rqiOverrides.entry_point,
        filesValue
      );
    } else if (jobDefinitionStr) {
      setConfig(orderedConfig, cloneRun.args, [], prunedFilesMap);
    }
  }, [
    queryRunQueueItem,
    cloneRun,
    jobDefinitionStr,
    runQueueItemToClone,
    jobMetadata,
    filesMap,
    usesJobArtifactMetadata,
    setConfig,
  ]);

  // when run queue item query changes because of cloneRun change
  // update the template variable values
  useEffect(() => {
    // update variable values
    if (
      queryRunQueueItem.loading ||
      selectedQueue?.queueID !== queryRunQueueItem.runQueueItem?.queueId ||
      selectedQueue == null
    ) {
      return;
    }

    // update variables
    if (selectedQueue?.variables) {
      setTemplateVariables(selectedQueue.variables);
    }

    if (queryRunQueueItem.runQueueItem?.templateVariableValues) {
      const runVarValues = pruneTemplateVariableValues(
        queryRunQueueItem.runQueueItem.templateVariableValues,
        selectedQueue?.variables ?? []
      );
      const queueDefaults = getDefaultVariableValuesFromQueueVariables(
        selectedQueue.variables
      );
      const mergedValues = {...queueDefaults, ...runVarValues};

      setVariableValues(mergedValues);
      return;
    }
    if (selectedQueue?.variables) {
      setVariableValues(
        getDefaultVariableValuesFromQueueVariables(selectedQueue.variables)
      );
    }
  }, [
    queryRunQueueItem.loading,
    queryRunQueueItem.runQueueItem?.queueId,
    queryRunQueueItem.runQueueItem?.templateVariableValues,
    selectedQueue,
  ]);

  // Set the resource config when the queue or variables change.
  useEffect(() => {
    if (!selectedQueue) {
      return;
    }
    const substitutedConfig = substituteVariables(
      selectedQueue.config,
      variableValues
    );
    setResourceConfig(
      displayMode === 'yaml'
        ? YAML.stringify(substitutedConfig, {indent: 2})
        : JSON.stringify(substitutedConfig, null, 2)
    );
  }, [selectedQueue, displayMode, variableValues]);

  if (loading || queryTargetProjects.loading || metadataLoading) {
    return <Loader active name="job-drawer-inner-loader" />;
  }

  const validate = (
    overridesToValidate: string,
    resourceConfigToValidate: string
  ): boolean => {
    let ok = true;
    try {
      JSON.parse(overridesToValidate);
    } catch (err) {
      setOverridesError('Overrides must be valid ');
      ok = false;
    }
    const rc = resourceConfigToValidate.trim() || '{}';
    try {
      if (displayMode === 'yaml') {
        YAML.parse(rc);
      } else {
        JSON.parse(rc);
      }
    } catch (err) {
      setResourceConfigError(
        'Resource config must be valid ' + displayMode.toUpperCase()
      );
      ok = false;
    }
    return ok;
  };

  const onLaunch = () => {
    let overridesToSend = overrides;
    if (overridesMode === OverridesMode.BASIC && jobMetadata.input_schemas) {
      overridesToSend = JSON.stringify(
        {
          args: [],
          entry_point: [],
          run_config: structuredOverrides,
          files: structuredFileOverrides,
        },
        null,
        4
      );
    }
    if (onFinalize != null) {
      if (!validate(overridesToSend, resourceConfig)) {
        return;
      }
      const rc = JSON.parse(resourceConfig);
      const resource = selectedQueue!.resourceType ?? 'local-container';
      let launchSpec = {
        entity: targetEntityName,
        project: targetProjectName,
        queue: selectedQueue!.queue,
        queueEntity: selectedQueue!.entity,
        job: `${entityName}/${projectName}/${job.name}:${job.versionInfo.runTarget}`,
        overrides: JSON.parse(overridesToSend),
        priority: effectivePriority,
        template_variables: variableValues,
      } as LaunchSpec;
      // if not empty, add resource_args
      if (Object.keys(rc).length > 0) {
        launchSpec = {...launchSpec, resource_args: {[resource]: rc}};
      }
      onFinalize(launchSpec);
      return;
    }
    // actually pushing job to queue
    setIsSubmitting(true);
    if (validate(overridesToSend, resourceConfig)) {
      pushRunToQueue(overridesToSend);
      Analytics.track('clicked launch now', {
        entityName,
        projectName,
        resourceConfig,
        location: 'job drawer',
      });
    } else {
      setIsSubmitting(false);
    }
  };

  const pushRunToQueue = async (overridesToSend: string) => {
    if (!selectedQueue) {
      setIsSubmitting(false);
      return;
    }
    let queueName = selectedQueue.queue;
    // creationEntity is the entity a new queue would be created in.
    const creationEntity =
      entityName === WANDB_ENTITY_NAME ? viewerEntity : entityName;
    const queueEntity = selectedQueue.entity || creationEntity;
    let queueID = selectedQueue.queueID;
    if (selectedQueue.queue === '') {
      // Create a starter queue
      // First, ensure we have the necessary project for an entity-scoped queue.
      const createProjectResponse = await upsertProject({
        variables: {
          entityName: creationEntity,
          name: DEFAULT_LAUNCH_PROJECT_NAME,
        },
      });
      if (createProjectResponse.errors != null) {
        toast('Error creating entity queue project.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'upsertProject',
          errors: createProjectResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }

      const completeConfig = {
        resource_args: {
          'local-container': {},
        },
      };
      const createConfigResponse = await createDefaultResourceConfig({
        variables: {
          entityName: creationEntity,
          resource: 'local-container',
          config: JSON.stringify(completeConfig),
          templateVariables: '{}',
        },
      });
      if (createConfigResponse.errors != null) {
        toast('Error creating entity queue config.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'createDefaultResourceConfig',
          errors: createConfigResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }
      const defaultResourceConfigID =
        createConfigResponse.data?.createDefaultResourceConfig
          ?.defaultResourceConfigID;

      queueName = 'Starter queue';
      const createQueueResponse = await createRunQueue({
        variables: {
          entityName: creationEntity,
          projectName: DEFAULT_LAUNCH_PROJECT_NAME,
          queueName,
          access: RunQueueAccessType.Project,
          defaultResourceConfigID,
          prioritizationMode: RunQueuePrioritizationMode.Disabled,
        },
      });
      if (createQueueResponse.errors != null) {
        toast('Error creating entity queue.');
        Analytics.track('create run queue error', {
          entityName: creationEntity,
          queueName,
          query: 'createRunQueue',
          errors: createConfigResponse.errors,
          location: 'job drawer',
        });
        setIsSubmitting(false);
        return;
      }
      Analytics.track('create run queue success', {
        entityName: creationEntity,
        queueName,
        location: 'job drawer',
      });
      queueID = createQueueResponse.data?.createRunQueue?.queueID ?? '';
    }

    const config: JsonObject = {
      entity: targetEntityName,
      project: targetProjectName,
      job: `${entityName}/${projectName}/${job.name}:${job.versionInfo.runTarget}`,
      overrides: JSON.parse(overridesToSend),
    };
    if (selectedQueue.resourceType) {
      config.resource_args = {
        [selectedQueue.resourceType]:
          displayMode === 'json'
            ? JSON.parse(resourceConfig)
            : YAML.parse(resourceConfig),
      };
    }

    const pushingConfig = config;
    if (queueUsingTemplateVariables) {
      delete pushingConfig.resource_args;
    }
    const pushedRunSpec = JSON.stringify(pushingConfig);
    try {
      const pushResp = await pushToRunQueue({
        variables: {
          queueID,
          runSpec: pushedRunSpec,
          priority: effectivePriority,
          templateVariableValues: templateVariables
            ? JSON.stringify(variableValues)
            : null,
        },
      });
      const newItemId = pushResp.data?.pushToRunQueue?.runQueueItemId;
      if (newItemId) {
        toast.success(
          <S.SuccessfulQueue>
            <IconInfo width={48} height={48} />
            <S.SuccessfulQueueMessage>
              Your job was successfully added to '{queueName}'.{' '}
              <TargetBlank
                data-test="view-queue-link"
                href={queueUrl(queueEntity, queueID, 'runs')}>
                View queue
              </TargetBlank>
            </S.SuccessfulQueueMessage>
          </S.SuccessfulQueue>
        );
        setIsSubmitting(false);
      } else {
        throw new Error();
      }
    } catch (err) {
      const errorMsg: string =
        (err as any)?.networkError?.result?.errors?.[0]?.message ??
        'Something went wrong while trying to queue this job.';
      toast.error(errorMsg);
      setIsSubmitting(false);
      return;
    }
    onClose();
  };

  // Don't show resource configuration if creating a starter queue
  const hasResourceConfig =
    selectedQueue && selectedQueue.resourceType && selectedQueue.queue !== '';

  return (
    <>
      {queryRunQueueItem.loading && (
        <Loader active name="job-drawer-inner-loader" />
      )}
      <Tailwind>
        <S.JobDrawerInnerMain>
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Clone from
            </div>
          </div>
          <Select
            id="clone-from-select"
            inputValue={cloneRun?.displayName}
            onMenuOpen={() => {
              setAnchorEl(document.getElementById('clone-from-select'));
            }}
            menuIsOpen={false}
          />
          {!lockedRunToClone && (
            <Popover
              id="job-drawer-paste-from-popover"
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={onPopoverClose}
              // TODO: Can this be translated to tailwind?
              PaperProps={{
                style: {
                  marginTop: '8px',
                  width:
                    document.getElementById('clone-from-select')?.offsetWidth ??
                    0,
                },
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}>
              <SelectRunPanel
                runs={previousRuns}
                customRunColors={customRunColors}
                onSelectRun={setCloneRun}
                onClose={onPopoverClose}
                locked={lockedRunToClone}
              />
            </Popover>
          )}
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Job overrides
            </div>
            <div style={{flex: 1}}></div>
            {jobMetadata.input_schemas && (
              <div className="flex flex-none justify-start">
                <Button
                  variant={variantBasic}
                  size="small"
                  onClick={() => setOverridesMode(OverridesMode.BASIC)}>
                  Basic
                </Button>
                <Button
                  variant={variantAdvanced}
                  size="small"
                  onClick={() => setOverridesMode(OverridesMode.ADVANCED)}
                  data-test="overrides-advanced">
                  Advanced
                </Button>
              </div>
            )}
          </div>
          {overridesMode === OverridesMode.BASIC &&
            jobMetadata.input_schemas && (
              <JobDrawerStructuredOverrides
                overrides={structuredOverrides}
                fileOverrides={structuredFileOverrides}
                onStructuredOverridesChange={onStructuredOverridesChange}
                onSelectRun={setCloneRun}
                schemas={jobMetadata.input_schemas}
              />
            )}
          {(overridesMode === OverridesMode.ADVANCED ||
            !jobMetadata.input_schemas) && (
            <JobDrawerOverrides
              overrides={overrides}
              onOverridesChange={onOverridesChange}
              onSelectRun={setCloneRun}
            />
          )}
          {overrideHintText && <S.HintText>{overrideHintText}</S.HintText>}
          {overridesError && <S.Error>{overridesError}</S.Error>}
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Queue
            </div>
          </div>
          <SelectQueue
            options={queues}
            selectedOption={selectedQueue}
            onSelectQueue={onSelectQueue}
          />
          <S.HintText>
            Inactive queues are unavailable for selection.
          </S.HintText>
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Job Priority
            </div>
          </div>
          <SelectPriority
            value={effectivePriority}
            disabled={!queueSupportsPrioritization}
            onSelectPriority={setPriority}
          />
          {!queueSupportsPrioritization ? (
            <S.HintText>
              The selected queue does not support job prioritization.
            </S.HintText>
          ) : (
            <S.PriorityHints>
              <S.PriorityHint>
                <Icon name="checkmark-circle" width={18} height={20} />
                {priorityMetadata.placementText}
              </S.PriorityHint>
              {/* TODO(np): Hide this until we actually support preemption

<S.PriorityHint>
<Icon name="checkmark-circle" width={18} height={20} />
{priorityMetadata.adminApprovalText}
</S.PriorityHint>

<S.PriorityHint>
<Icon name="checkmark-circle" width={18} height={20} />
{priorityMetadata.preemptionText}
</S.PriorityHint>
*/}
            </S.PriorityHints>
          )}
          {hasResourceConfig && (
            <>
              {sortedTemplateVariables.length > 0 && (
                <div className="mb-4 mt-16 flex items-center">
                  <div className="text-[1rem] font-semibold leading-[2rem]">
                    Queue Configurations
                  </div>
                </div>
              )}
              {sortedTemplateVariables.length > 0 &&
                !queryRunQueueItem.loading &&
                sortedTemplateVariables.map((variable, i) => (
                  <ResourceVariableForm
                    key={i}
                    name={variable.name}
                    schema={variable.schema}
                    description={variable.description}
                    value={variableValues[variable.name]}
                    updateVariable={updateVariable}
                  />
                ))}
              <div className="mb-4 mt-16 flex items-center">
                <div>Complete queue configurations</div>
                <S.ToggleConfig>
                  {showResourceConfig && (
                    <Button
                      variant={variantYaml}
                      onClick={() => setEditorType('yaml')}>
                      YAML
                    </Button>
                  )}
                  {showResourceConfig && (
                    <Button
                      variant={variantJson}
                      onClick={() => setEditorType('json')}>
                      JSON
                    </Button>
                  )}
                  <Button
                    data-test="show-resource-config"
                    variant="ghost"
                    onClick={() => {
                      setShowResourceConfig(!showResourceConfig);
                    }}>
                    {showResourceConfig ? 'Hide' : 'Show'}
                  </Button>
                </S.ToggleConfig>
              </div>
              {showResourceConfig && (
                <CodeEditor
                  value={resourceConfig}
                  language={displayMode}
                  onChange={onResourceConfigChange}
                  readOnly={queueUsingTemplateVariables}
                />
              )}
              {resourceConfigError && <S.Error>{resourceConfigError}</S.Error>}
            </>
          )}
          <div className="mb-4 mt-16 flex items-center">
            <div className="text-[1rem] font-semibold leading-[2rem]">
              Destination project
            </div>
          </div>
          <SelectProject
            defaultEntityName={targetEntityName}
            defaultProjectName={targetProjectName}
            options={queryTargetProjects.options}
            onSelectProject={onSelectProject}
          />
          {selectedQueue == null && (
            <S.HintText>Select a queue to see available projects.</S.HintText>
          )}
          {children}
        </S.JobDrawerInnerMain>
      </Tailwind>
      <Button
        data-test="launch-button"
        className="w-full"
        variant="primary"
        size="large"
        onClick={onLaunch}
        disabled={
          !selectedQueue ||
          !targetEntityName ||
          !targetProjectName ||
          isSubmitting ||
          queryRunQueueItem.loading
        }>
        {finalizeCTA ?? 'Launch now'}
      </Button>
    </>
  );
};
